import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  IconButton,
  Text,
  TextField,
} from '@vibe/core';
import { Add, Delete, Search } from '@vibe/icons';
import {
  restrictUser,
  unrestrictUser,
  type UserAccess,
} from '../../api/settings.api';
import UserPicker, { type PickedUser } from '../shared/UserPicker';

// ─── Props ────────────────────────────────────────────────────────────────────

interface RestrictUsersTabProps {
  users: UserAccess[];
  onUpdateUser: (user: UserAccess) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const RestrictUsersTab: React.FC<RestrictUsersTabProps> = ({
  users,
  onUpdateUser,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PickedUser | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Derived data ─────────────────────────────────────────────────────────

  const restrictedUsers = useMemo(() => {
    const restricted = users.filter((u) => u.isRestricted);
    if (!searchQuery.trim()) return restricted;
    const q = searchQuery.toLowerCase();
    return restricted.filter(
      (u) =>
        (u.userName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q),
    );
  }, [users, searchQuery]);

  // ─── Restrict user ────────────────────────────────────────────────────────

  const handleRestrictUser = useCallback(async () => {
    if (!selectedUser) return;
    setIsAdding(true);
    setError(null);
    try {
      const updated = await restrictUser(selectedUser.id);
      onUpdateUser({ ...updated, userName: selectedUser.name, email: selectedUser.email });
      setSelectedUser(null);
      setShowAddForm(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to restrict user';
      setError(message);
    } finally {
      setIsAdding(false);
    }
  }, [selectedUser, onUpdateUser]);

  // ─── Unrestrict user ──────────────────────────────────────────────────────

  const handleUnrestrictUser = useCallback(
    async (mondayUserId: string) => {
      setError(null);
      try {
        const updated = await unrestrictUser(mondayUserId);
        onUpdateUser(updated);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to unrestrict user';
        setError(message);
      }
    },
    [onUpdateUser],
  );

  // ─── Get role label ───────────────────────────────────────────────────────

  const getRoleLabel = (user: UserAccess): string => {
    if (user.isAdmin) return 'Admin';
    return 'Member';
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="rp-restrict-users">
      {/* Action bar */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-restrict-users__action-bar"
      >
        <Button
          size={Button.sizes.SMALL}
          leftIcon={Add}
          onClick={() => setShowAddForm(true)}
        >
          Restrict
        </Button>
        <TextField
          size={TextField.sizes.SMALL}
          placeholder="Search for users"
          value={searchQuery}
          onChange={(value: string) => setSearchQuery(value)}
          iconName={Search}
          className="rp-restrict-users__search"
        />
      </Flex>

      {/* Add restricted user form */}
      {showAddForm && (
        <div className="rp-permission-mgmt__add-form">
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            Restrict a user
          </Text>
          <Flex gap={Flex.gaps.SMALL} align={Flex.align.END}>
            <div style={{ flex: 1 }}>
              <UserPicker
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="Search for a user…"
              />
            </div>
            <Button
              size={Button.sizes.SMALL}
              onClick={handleRestrictUser}
              loading={isAdding}
              disabled={!selectedUser}
            >
              Restrict
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              onClick={() => {
                setShowAddForm(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
          </Flex>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rp-permission-mgmt__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      {/* Table */}
      {restrictedUsers.length === 0 ? (
        <div className="rp-permission-mgmt__empty">
          <div className="rp-permission-mgmt__empty-illustration">
            <svg
              width="80"
              height="120"
              viewBox="0 0 80 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="28" r="14" fill="#c3c4c7" />
              <rect x="20" y="48" width="40" height="55" rx="6" fill="#1f3e72" />
              <rect x="26" y="48" width="28" height="30" rx="4" fill="#2b2c5e" />
              <line x1="40" y1="10" x2="48" y2="4" stroke="#323338" strokeWidth="2" />
              <line x1="40" y1="10" x2="50" y2="8" stroke="#323338" strokeWidth="2" />
            </svg>
          </div>
          <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
            Nothing found
          </Text>
        </div>
      ) : (
        <table className="rp-settings-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th>Name</th>
              <th>Title</th>
              <th>Role</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {restrictedUsers.map((user, index) => (
              <tr key={user.mondayUserId}>
                <td>
                  <Text type={Text.types.TEXT2}>{index + 1}</Text>
                </td>
                <td>
                  <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
                    <div
                      className="rp-permission-mgmt__avatar"
                      style={{
                        backgroundColor: user.isAdmin ? '#e44258' : '#fdab3d',
                      }}
                    >
                      <Text
                        type={Text.types.TEXT2}
                        weight={Text.weights.BOLD}
                        style={{ color: '#fff', fontSize: 11 }}
                      >
                        {getInitials(user.userName)}
                      </Text>
                    </div>
                    <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                      {user.userName}
                    </Text>
                  </Flex>
                </td>
                <td>
                  <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                    —
                  </Text>
                </td>
                <td>
                  <span className="rp-permission-mgmt__role-badge">
                    {getRoleLabel(user)}
                  </span>
                </td>
                <td>
                  <IconButton
                    icon={Delete}
                    size={IconButton.sizes.SMALL}
                    onClick={() => handleUnrestrictUser(user.mondayUserId)}
                    ariaLabel={`Unrestrict ${user.userName}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name[0] || '?').toUpperCase();
}

export default RestrictUsersTab;
